// @mui
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography, CircularProgress, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState, useRef } from 'react';
// components
import Iconify from 'src/components/iconify';
import { getRequest, postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import useProductFeatureSection from 'src/hooks/use-product-feature-page';

// ----------------------------------------------------------------------

export default function FinancialManagementSection() {
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});


   const productFeaturePageData = useSelector((state) => state.website.productFeaturePage);
    const { fetchOnMount } = useProductFeatureSection();
    const { enqueueSnackbar } = useSnackbar();


  const [formData, setFormData] = useState({
    // Close Out Section
    closeOutSection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: [
        { title: "", iconUrl: "", backgroundColor: "#ffffff", textColor: "#000000" },
      ]
    },

    // Document Control Section
    documentControlSection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: [
        { title: "", iconUrl: "", backgroundColor: "#ffffff", textColor: "#000000" },
      ]
    },

    // Financial Management Section
    financialManagementSection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: [
        { title: "", iconUrl: "", backgroundColor: "#ffffff", textColor: "#000000" },
      ]
    },

    // Technology / Product Integration Section
    technologyIntegrationSection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: [
        { title: "", iconUrl: "", backgroundColor: "#ffffff", textColor: "#000000" },
      ]
    },

    // Reports & Analytics Section
    reportsAnalyticsSection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: [
        { title: "", iconUrl: "", backgroundColor: "#ffffff", textColor: "#000000" },
      ]
    },

    // Post Construction Section
    postConstructionSection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: [
        { title: "", iconUrl: "", backgroundColor: "#ffffff", textColor: "#000000" }
      ]
    },

    // Software Development Section
    softwareDevelopmentSection: {
      title: "",
      titleColor: "#000000",
      subtitle: "",
      subtitleColor: "#666666",
      description: "",
      descriptionColor: "#555555",
      features: [
        { title: "", iconUrl: "", backgroundColor: "#ffffff", textColor: "#000000" },
      ]
    }
  });

  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (typeof current[key] !== 'object' || Array.isArray(current[key])) {
          current[key] = {};
        }
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  const updateFeature = (sectionName, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        features: prev[sectionName].features.map((feature, i) =>
          i === index ? { ...feature, [field]: value } : feature
        )
      }
    }));
  };

  const addFeature = (sectionName) => {
    const newFeature = {
      id: `feature-${Date.now()}`,
      title: "",
      iconUrl: "",
      backgroundColor: "#ffffff",
      textColor: "#000000"
    };

    setFormData(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        features: [...prev[sectionName].features, newFeature]
      }
    }));
  };

  const removeFeature = (sectionName, index) => {
    setFormData(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        features: prev[sectionName].features.filter((_, i) => i !== index)
      }
    }));
  };


  useEffect(() => {
      fetchOnMount();
    }, []);
    useEffect(() => {
      if (productFeaturePageData?.financial_Management_section) {
        // Create a deep copy of the community section to avoid read-only property issues
        const projectManagementSection = JSON.parse(JSON.stringify(productFeaturePageData.financial_Management_section));
        setFormData(prev => ({
          ...prev,
          ...projectManagementSection
        }));
      }
    }, [productFeaturePageData])



  const handleSave = async () => {
    setLoading(true);

        try {
          // Create a FormData object for file uploads
          const formDataToSend = new FormData();

          // Append all form data
          Object.entries(formData).forEach(([key, value]) => {
            // Skip heroImage as we'll handle it separately
            if (key !== 'heroImage') {
              // Handle nested objects like primaryButton and secondaryButton
              if (typeof value === 'object' && value !== null && !(value instanceof File)) {
                formDataToSend.append(key, JSON.stringify(value));
              } else {
                formDataToSend.append(key, value);
              }
            }
          });

          // Append the hero image if it exists
          if (formData.heroImage) {
            formDataToSend.append('heroImage', formData.heroImage);
          }

          const response = await postRequestForWebsite(
            endpoints.admin_dashboard.website.product_feature_page.product_feature_section,
            // formDataToSend,
            // dataForHeroSection,
            {
              attributes: {
                ...JSON.parse(JSON.stringify(productFeaturePageData)),
                financial_Management_section: JSON.parse(JSON.stringify(formData))
              }
            },
            true
          );

          if (response.metadata?.success) {
            enqueueSnackbar({
              variant: 'success',
              message: response.metadata.message,
            });
          } else {
            handleErrorMessages(response.message || 'An error occurred.');
          }
        } catch (err) {
          console.error('Error:', err);
          handleErrorMessages(err.message || 'Submission failed.');
        } finally {
          setLoading(false);
        }
  };

  const handleAccordionChange = (section) => (event, isExpanded) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: isExpanded
    }));
  };

  const renderSectionHeader = (title) => (
    <Stack direction="row" alignItems="center" sx={{ mb: 0 }}>
      <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
      <Typography variant="h6" color="primary">{title}</Typography>
    </Stack>
  );

  const renderFeatureGrid = (features, sectionName, title) => (
    <Box sx={{ mt: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
        <Button
          variant="contained"
          color="success"
          size="small"
          startIcon={<Iconify icon="mingcute:add-line" />}
          onClick={() => addFeature(sectionName)}
        >
          Add Feature
        </Button>
      </Stack>

      <Grid container spacing={2}>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: 'background.neutral', minHeight: 280 }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="caption" color="primary">
                  Feature {index + 1}
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => removeFeature(sectionName, index)}
                  sx={{ minWidth: 25, p: 0.2 }}
                >
                  ×
                </Button>
              </Stack>

              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  label="Feature Title"
                  size="small"
                  value={feature.title}
                  onChange={(e) => updateFeature(sectionName, index, 'title', e.target.value)}
                  placeholder="Enter feature title"
                />

                <TextField
                  fullWidth
                  label="Icon URL"
                  size="small"
                  value={feature.iconUrl}
                  onChange={(e) => updateFeature(sectionName, index, 'iconUrl', e.target.value)}
                  placeholder="Enter icon URL"
                />

                <TextField
                  fullWidth
                  label="Background Color"
                  type="color"
                  size="small"
                  value={feature.backgroundColor}
                  onChange={(e) => updateFeature(sectionName, index, 'backgroundColor', e.target.value)}
                  sx={{ '& input': { height: 30 } }}
                />

                <TextField
                  fullWidth
                  label="Text Color"
                  type="color"
                  size="small"
                  value={feature.textColor}
                  onChange={(e) => updateFeature(sectionName, index, 'textColor', e.target.value)}
                  sx={{ '& input': { height: 30 } }}
                />

                {/* Icon Preview */}
                {feature.iconUrl && (
                  <Box
                    sx={{
                      width: '100%',
                      height: 50,
                      bgcolor: feature.backgroundColor,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      component="img"
                      src={feature.iconUrl}
                      alt={`${feature.title} Icon`}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                      sx={{
                        width: 24,
                        height: 24,
                        objectFit: 'contain',
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        display: 'none',
                        color: 'text.disabled',
                        fontSize: 10
                      }}
                    >
                      Invalid URL
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderSectionContent = (sectionKey, sectionData, sectionTitle) => (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={3}>
        {/* Title */}
        <Grid item xs={12} md={10}>
          <TextField
            fullWidth
            label="Section Title"
            value={sectionData.title}
            onChange={(e) => updateFormData(`${sectionKey}.title`, e.target.value)}
            placeholder="Enter section title"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label="Title Color"
            type="color"
            value={sectionData.titleColor}
            onChange={(e) => updateFormData(`${sectionKey}.titleColor`, e.target.value)}
            sx={{ '& input': { height: 20 } }}
          />
        </Grid>

        {/* Subtitle */}
        <Grid item xs={12} md={10}>
          <TextField
            fullWidth
            label="Section Subtitle"
            value={sectionData.subtitle}
            onChange={(e) => updateFormData(`${sectionKey}.subtitle`, e.target.value)}
            placeholder="Enter section subtitle"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label="Subtitle Color"
            type="color"
            value={sectionData.subtitleColor}
            onChange={(e) => updateFormData(`${sectionKey}.subtitleColor`, e.target.value)}
            sx={{ '& input': { height: 20 } }}
          />
        </Grid>

        {/* Description */}
        <Grid item xs={12} md={10}>
          <TextField
            fullWidth
            label="Section Description"
            multiline
            rows={3}
            value={sectionData.description}
            onChange={(e) => updateFormData(`${sectionKey}.description`, e.target.value)}
            placeholder="Enter section description"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            label="Description Color"
            type="color"
            value={sectionData.descriptionColor}
            onChange={(e) => updateFormData(`${sectionKey}.descriptionColor`, e.target.value)}
            sx={{ '& input': { height: 20 } }}
          />
        </Grid>
      </Grid>

      {/* Features */}
      {sectionData.features && sectionData.features.length > 0 && (
        renderFeatureGrid(sectionData.features, sectionKey, `${sectionTitle} Features`)
      )}
    </Box>
  );

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
          <Typography variant="h4">
            Website Sections Management
          </Typography>
          <Button
            variant="outlined"
            onClick={() => {
              const allExpanded = Object.keys(formData).reduce((acc, key) => {
                acc[key] = true;
                return acc;
              }, {});
              setExpandedSections(allExpanded);
            }}
          >
            Expand All
          </Button>
        </Stack>

        <Stack spacing={1}>
          {/* Close Out Section */}
          <Accordion
            expanded={expandedSections.closeOutSection || false}
            onChange={handleAccordionChange('closeOutSection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Close Out Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('closeOutSection', formData.closeOutSection, 'Close Out')}
            </AccordionDetails>
          </Accordion>

          {/* Document Control Section */}
          <Accordion
            expanded={expandedSections.documentControlSection || false}
            onChange={handleAccordionChange('documentControlSection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Document Control Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('documentControlSection', formData.documentControlSection, 'Document Control')}
            </AccordionDetails>
          </Accordion>

          {/* Financial Management Section */}
          <Accordion
            expanded={expandedSections.financialManagementSection || false}
            onChange={handleAccordionChange('financialManagementSection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Financial Management Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('financialManagementSection', formData.financialManagementSection, 'Financial Management')}
            </AccordionDetails>
          </Accordion>

          {/* Technology / Product Integration Section */}
          <Accordion
            expanded={expandedSections.technologyIntegrationSection || false}
            onChange={handleAccordionChange('technologyIntegrationSection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Technology / Product Integration Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('technologyIntegrationSection', formData.technologyIntegrationSection, 'Technology Integration')}
            </AccordionDetails>
          </Accordion>

          {/* Reports & Analytics Section */}
          <Accordion
            expanded={expandedSections.reportsAnalyticsSection || false}
            onChange={handleAccordionChange('reportsAnalyticsSection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Reports & Analytics Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('reportsAnalyticsSection', formData.reportsAnalyticsSection, 'Reports & Analytics')}
            </AccordionDetails>
          </Accordion>

          {/* Post Construction Section */}
          <Accordion
            expanded={expandedSections.postConstructionSection || false}
            onChange={handleAccordionChange('postConstructionSection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Post Construction Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('postConstructionSection', formData.postConstructionSection, 'Post Construction')}
            </AccordionDetails>
          </Accordion>

          {/* Software Development Section */}
          <Accordion
            expanded={expandedSections.softwareDevelopmentSection || false}
            onChange={handleAccordionChange('softwareDevelopmentSection')}
          >
            <AccordionSummary expandIcon={<GridExpandMoreIcon />}>
              {renderSectionHeader('Software Development Section')}
            </AccordionSummary>
            <AccordionDetails>
              {renderSectionContent('softwareDevelopmentSection', formData.softwareDevelopmentSection, 'Software Development')}
            </AccordionDetails>
          </Accordion>
        </Stack>

        {/* Action Buttons */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ pt: 4, mt: 0, borderTop: '1px solid', borderColor: 'divider' }}
        >
          {/* <Button
            variant="outlined"
            color="secondary"
            size="large"
            onClick={() => {
              // Reset form or add preview functionality
              console.log("Preview functionality");
            }}
          >
            Preview Changes
          </Button> */}
          <LoadingButton
            loading={loading}
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<Iconify icon="eva:save-fill" />}
            size="large"
          >
            Update
          </LoadingButton>
        </Stack>
      </CardContent>
    </Card>
  );
}
